<template>
    <div class="service-providers-write-message-step w-100 h-100">
        <b-row
                class="h-100"
                no-gutters
        >
            <b-col
                    :cols="12"
                    :md="6"
                    class="form-column"
            >
                <div class="form-wrapper w-100">
                    <div class="form-icon-wrapper">
                        <i class="far fa-comments-alt form-icon" />
                    </div>

                    <div class="form-title">
                        Get Connected
                    </div>

                    <div class="form-subtitle">
                        Contact us for a no-obligation consultation, or visit our website.
                    </div>

                    <get-connected-form
                            v-model="message"
                            :errors="messageErrors"
                            :fields="formFields"
                    />

                    <div class="cta-buttons-wrapper">
                        <b-button
                                size="lg"
                                variant="transparent"
                                class="shadow-none bg-white cancel-button"
                                @click="handleCancel"
                        >
                            Cancel
                        </b-button>

                        <b-button
                                text="Save"
                                size="lg"
                                variant="primary"
                                class="save-button d-flex justify-content-center"
                                :disabled="!canSend"
                                @click="handleSend"
                        >
                            <b-spinner
                                    v-if="isLoading"
                                    class="mr-1"
                                    small
                            />
                            Send
                        </b-button>
                    </div>

                    <div class="disclaimer d-block">
                        By sending this message, you agree to the
                        <br v-if="isMobile">
                        <b-link
                                :href="termsOfUseLink"
                                target="_blank"
                        >
                            Terms of Use.
                        </b-link>
                    </div>
                </div>
            </b-col>
            <b-col
                    :cols="12"
                    :md="6"
                    class="info-card-column"
            >
                <div class="info-card-wrapper">
                    <service-provider-card
                            :service-provider="serviceProvider"
                            variant="detailed"
                    />

                    <div class="disclaimer d-none d-md-block">
                        The websites listed above are independent and we do not guarantee the accuracy, completeness,
                        efficiency, or timeliness of the information contained therein.
                    </div>
                </div>
            </b-col>
        </b-row>
    </div>
</template>

<script>
    import './WriteMessageStep.scss';
    import GetConnectedForm, {FIELDS} from "./GetConnectedForm/GetConnectedForm.vue";
    import ServiceProviderCard from "../../ServiceProviderCard/ServiceProviderCard.vue";
    import ServiceProviderAppResource from "@milestoneslabs/milestones-core-js/src/AppResources/ServiceProviderAppResource";
    import isEmpty from "lodash/isEmpty";
    import ShowNotificationMixin from "../../../../mixins/ShowNotificationMixin";
    import ServiceProviderMessageAppResource
        from "@milestoneslabs/milestones-core-js/src/AppResources/ServiceProviderMessageAppResource";
    import ResourceAppResource from "@milestoneslabs/milestones-core-js/src/AppResources/ResourceAppResource";
    import IsMobileMixin from "../../../../mixins/IsMobileMixin";
    import UserAppResource from "@milestoneslabs/milestones-core-js/src/AppResources/UserAppResource";
    import WorkspaceAppResource from "@milestoneslabs/milestones-core-js/src/AppResources/WorkspaceAppResource";
    import OrganizationMemberAppResource
        from "@milestoneslabs/milestones-core-js/src/AppResources/OrganizationMemberAppResource";
    import LandingPageAppResource from "@milestoneslabs/milestones-core-js/src/AppResources/LandingPageAppResource";
    import AppResourceCollection from "@milestoneslabs/milestones-js-engine/src/Data/AppResourceCollection";
    import HomeownersInsuranceAppResource
        from "@milestoneslabs/milestones-core-js/src/AppResources/HomeownersInsuranceAppResource";

    const FORM_FIELDS = [
        FIELDS.FROM,
        FIELDS.EMAIL,
        FIELDS.PHONE,
        FIELDS.RESPOND_BY,
        FIELDS.MESSAGE,
    ];
    export default {
        name: "WriteMessageStep",
        components: {
            ServiceProviderCard,
            GetConnectedForm
        },

        mixins: [
            ShowNotificationMixin,
            IsMobileMixin,
        ],

        props: {
            serviceProvider: {
                type: ServiceProviderAppResource,
                required: true,
            },

            resource: {
                type: ResourceAppResource,
                required: true,
            },

            senderUser: {
                type: UserAppResource,
                default: () => {
                    return new UserAppResource({});
                },
            },

            organizationMember: {
                type: OrganizationMemberAppResource,
                default: () => {
                    return new OrganizationMemberAppResource({});
                },
            },

            workspace: {
                type: WorkspaceAppResource,
                default: () => {
                    return new WorkspaceAppResource({});
                },
            },

            landingPage: {
                type: LandingPageAppResource,
                default: () => {
                    return new LandingPageAppResource({});
                },
            },

            termsOfUseLink: {
                type: String,
                default: 'https://milestones.ai/terms-of-use/',
            },

            prefillForm: {
                type: Boolean,
                default: false,
            },

            meta: {
                type: Object,
                default: () => {
                },
            },
            insurance: {
                type: HomeownersInsuranceAppResource,
                default: () => {
                    return new HomeownersInsuranceAppResource();
                }
            }
        },

        data() {
            return {
                formFields: [],
                message: new ServiceProviderMessageAppResource({
                    attributes: {
                        respondByEmail: true,
                        respondByPhone: true,
                        serviceProviderUuid: '',
                    },
                }),
                messageErrors: {},
                isLoading: false,
            };
        },

        computed: {
            canSend() {
                const fieldsFilled = !isEmpty(this.message.from) ||
                    !isEmpty(this.message.email) ||
                    !isEmpty(this.message.phone) ||
                    !isEmpty(this.message.message);

                return fieldsFilled && !this.isLoading;
            },
        },

        methods: {
            init() {
                this.initFormFields();
                this.message.policyUuid = this.insurance.policyFileUuid;
                if (
                    this.prefillForm &&
                    !isEmpty(this.senderUser) &&
                    this.senderUser.exists
                ) {
                    this.message.setAttribute('from', `${this.senderUser.firstName} ${this.senderUser.lastName}`);
                    this.message.setAttribute('email', this.senderUser.email);

                    if (!isEmpty(this.senderUser.phone)) {
                        this.message.setAttribute('phone', this.senderUser.phone);
                    }
                }

                this.message.setAttribute(
                    'message',
                    `Please contact me with more information about your services.`
                );
            },

            initFormFields() {
                this.formFields = FORM_FIELDS;
            },
            async handleSend() {
                this.isLoading = true;
                this.messageErrors = {};


                try {
                    const errors = this.message.validateMessage();
                    if (!isEmpty(errors)) {
                        this.messageErrors = errors;
                        return;
                    }

                    const message = this.setMessageData(this.serviceProvider.uuid);

                    await message.save(false);

                    message.once('saved', () => {
                        this.$emit('sent');
                    });
                } catch (e) {
                    console.log(e);
                    this.showNotification({
                        type: 'error',
                        message: 'Message could not be sent. Please, try again later.',
                    });
                } finally {
                    this.isLoading = false;
                }
            },

            setMessageData(serviceProviderUuid) {
                const message = new ServiceProviderMessageAppResource({
                    attributes: {
                        respondByEmail: this.message.respondByEmail,
                        respondByPhone: this.message.respondByPhone,
                        from: this.message.from,
                        email: this.message.email,
                        phone: this.message.phone,
                        message: this.message.message,
                        serviceProviderUuid: serviceProviderUuid,
                        resourceUuid: this.resource.uuid,
                        meta: this.meta
                    },
                });
                message.attachments = this.message.attachments;
                message.policyUuid = this.message.policyUuid;

                if (
                    !isEmpty(this.organizationMember) &&
                    this.organizationMember.exists
                ) {
                    message.setAttribute('organizationMemberUuid', this.organizationMember.uuid);
                }

                if (
                    !isEmpty(this.workspace) &&
                    this.workspace.exists
                ) {
                    message.setAttribute('workspaceUuid', this.workspace.uuid);
                }

                if (
                    !isEmpty(this.landingPage) &&
                    this.landingPage.exists
                ) {
                    message.setAttribute('landingPageUuid', this.landingPage.uuid);
                }

                return message;
            },

            handleCancel() {
                this.$emit('cancel');
            },
        },

        beforeMount() {
            this.init();
        },
    };
</script>
